import { GeoLocationErrorDialog } from '@/components/dialogs/geo-location-error-dialog/geo-location-error-dialog';
import { LocationIcon } from '@/components/icons/location-icon';
import { Button } from '@/components/ui/button';
import { useUpdateSharedState } from '@/hooks/state/shared-state';
import { useGeoTheaterSearch } from '@/hooks/theater-geo-location-search';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import clsx from 'clsx';
import React, { useState } from 'react';
import geoLocationStyles from './geo-location.module.scss';

type GeoLocationComponentProps = {
  className?: string;
  mini?: boolean;
  miniButtonVariant?: 'ghost' | 'link';
  miniIconClassName?: string;
  miniText?: string;
  miniTextClassName?: string;
  onResults?: (results: PagedListOfTheater) => void;
  searchType?: 'return-results' | 'search-and-redirect' | 'set-location';
};

export const GeoLocationComponent = ({
  className,
  mini,
  miniButtonVariant = 'link',
  miniIconClassName,
  miniText,
  miniTextClassName,
  onResults,
  searchType,
}: GeoLocationComponentProps): React.JSX.Element => {
  const [geoLocationError, setGeoLocationError] = useState<GeolocationPositionError>();
  const [searching, setSearching] = useState(false);
  const { searchByGeo } = useGeoTheaterSearch();
  const { mutateAsync: updateSharedState } = useUpdateSharedState();

  const locationSvg = <LocationIcon aria-hidden="true"
    className={clsx(
      miniIconClassName || 'icon-lg',
      mini ? 'fill-primary group-hover:fill-primary-darker' : 'icon-primary no-hover-effect',
      geoLocationStyles.icon,
      mini ? geoLocationStyles.mini : ''
    )}
    viewBox="0 0 20 20"
  />;

  const handleSearchByGeo = async (): Promise<void> => {
    setSearching(true);
    setGeoLocationError(undefined);
    await updateSharedState({ initialGeoAttempt: true });
    const result = await searchByGeo(searchType ?? (onResults ? 'return-results' : 'search-and-redirect'));
    if (result.error) {
      setGeoLocationError(result.error);
    } else {
      onResults?.(result.data);
    }

    setSearching(false);
  };

  if (mini) {
    return <>
      <Button aria-label={miniText || 'Use your location'}
        className={clsx('flex items-center gap-xs group', className)}
        data-test="button-geo-location-search"
        type="button"
        variant={miniButtonVariant}
        onClick={handleSearchByGeo}
      >
        {locationSvg}
        {miniText &&
          <span className={miniTextClassName || 'text-primary font-bold text-xs'}
            data-test="button-geo-location-text"
          >
            {miniText}
          </span>
        }
      </Button>

      <GeoLocationErrorDialog geoLocationError={geoLocationError} />
    </>;
  }

  return <>
    <div className={clsx('flex flex-col items-center justify-between gap-sm', className)}>
      <div className="flex flex-col items-center gap-sm">
        {locationSvg}
        <p data-test="find-showtimes-text">Find theatres near your location.</p>
      </div>

      <Button className="w-full"
        data-test="button-geo-location-search"
        disabled={searching}
        loadingCenter="within"
        showLoading={searching}
        type="button"
        onClick={handleSearchByGeo}
      >
        Use Your Location
      </Button>
    </div>

    <GeoLocationErrorDialog geoLocationError={geoLocationError} />
  </>;
};
